// DEMO ONLY. The support chats (Figma 6421:9962, 6422:10265, 6459:16869 / 17172 / 16542,
// 6423:10690) as page response blocks: the payloads a live answer would carry.
import {
  ClarifyingQuestionBlock,
  RenewalReviewRequestBlock,
  ResponseFormField,
  ResponseFormStatus,
  ResponseResult,
  SupportAction,
  SupportTicketFormBlock,
  SupportTicketFormResult,
  SupportTicketRecordBlock,
  SupportTicketTableBlock
} from '../../support/types';
import { IOrbitPageDemoChat, seedTurn } from './seedTurn';

const TICKET_CHIPS = ['Screen: Customer Insights', 'Period: Last 30 Days'];

const TICKET_QUESTION = 'Orbit, the campaign page is failing when I try to publish. Can you help?';

const TICKET_FIELDS: ResponseFormField[] = [
  {
    key: 'environment',
    label: 'Environment',
    kind: 'select',
    value: 'uat',
    required: true,
    options: [
      { value: 'uat', label: 'UAT' },
      { value: 'pre_production', label: 'Pre-production' },
      { value: 'production', label: 'Production' }
    ]
  },
  {
    key: 'domain',
    label: 'Domain',
    kind: 'select',
    value: 'dashboard',
    required: true,
    options: [
      { value: 'backend_api', label: 'Backend/API' },
      { value: 'dashboard', label: 'Dashboard' },
      { value: 'microsite', label: 'Microsite' },
      { value: 'data_report', label: 'Data report' }
    ]
  },
  { key: 'area', label: 'Area', kind: 'text', value: 'Campaigns › Publish' },
  { key: 'tenant', label: 'Tenant', kind: 'text', value: 'generic' },
  {
    key: 'detected_issue',
    label: 'Detected issue',
    kind: 'readonly_boxed',
    value: 'Campaign publish action fails from dashboard'
  },
  {
    key: 'description',
    label: 'Description',
    kind: 'readonly_boxed',
    value:
      'Publish action fails after campaign review. User remains on the same screen and the campaign is not published.'
  },
  {
    key: 'evidence',
    label: 'Evidence',
    kind: 'text',
    value: 'Screen context + session metadata attached'
  }
];

const CREATE_TICKET: SupportAction = {
  id: 'create_support_ticket',
  label: 'Create support ticket',
  kind: 'primary'
};

// 6459:16869 / 17172 / 16542: once submitted, Create stays greyed and Edit details is gone.
const TICKET_ACTIONS: Record<ResponseFormStatus, SupportAction[]> = {
  draft: [CREATE_TICKET, { id: 'edit_details', label: 'Edit details', kind: 'secondary' }],
  processing: [{ ...CREATE_TICKET, disabled: true }],
  success: [{ ...CREATE_TICKET, disabled: true }],
  error: [{ ...CREATE_TICKET, disabled: true }]
};

const TICKET_RESULTS: Record<ResponseFormStatus, SupportTicketFormResult | null> = {
  draft: null,
  processing: { title: 'Processing......' },
  success: {
    title: 'Ticket created successfully! Perx support team will contact you shortly.',
    ticket_id: { label: 'Ticket ID:', value: 'TKT-1087' },
    submitted_at: { label: 'Submitted:', value: '2026-04-04T11:48:00+08:00' }
  },
  error: { title: 'Something went wrong. Try again later!' }
};

const ticketForm = (status: ResponseFormStatus): SupportTicketFormBlock => ({
  type: 'support_ticket_form',
  id: `ticket-form-${status}`,
  intro:
    'I can help raise this with Support. I detected the most relevant technical context from your session. Please review it before creating the ticket.',
  title: 'Create Support Ticket',
  icon: 'ticket',
  status,
  fields: TICKET_FIELDS,
  actions: TICKET_ACTIONS[status],
  result: TICKET_RESULTS[status]
});

const ticketRow = (
  id: string,
  title: string,
  severity: string,
  status: { value: string; tone: 'info' | 'warning' },
  opened: string
) => ({
  id,
  cells: {
    id: { value: id },
    title: { value: title },
    severity: { value: severity, tone: 'warning' as const },
    status,
    opened: { value: opened }
  },
  action: {
    id: 'open_ticket',
    label: `Open ticket ${id}`,
    kind: 'link' as const,
    payload: { ticket_id: id }
  }
});

const CLARIFYING_QUESTION: ClarifyingQuestionBlock = {
  type: 'clarifying_question',
  id: 'ticket-environment',
  question: 'In which environment are you facing this issue?'
};

const TICKET_TABLE: SupportTicketTableBlock = {
  type: 'support_ticket_table',
  id: 'tickets-active',
  intro: 'Here are your active support tickets.',
  title: 'Active Support Tickets (3)',
  icon: 'ticket',
  // 6265:4230-4238: 120.4 / 269 / 120.1 / 151.2 / 142.3 of 803.
  columns: [
    { key: 'id', label: 'Ticket ID', kind: 'id', width: 0.15 },
    { key: 'title', label: 'Title', kind: 'text', width: 0.335 },
    { key: 'severity', label: 'Severity', kind: 'pill', width: 0.15 },
    { key: 'status', label: 'Status', kind: 'status', width: 0.188 },
    { key: 'opened', label: 'Opened', kind: 'date', width: 0.177 }
  ],
  rows: [
    ticketRow(
      'PRX-8491',
      'Campaign publish failure',
      'P2',
      { value: 'Open', tone: 'info' },
      '2026-09-05T14:32:00+08:00'
    ),
    ticketRow(
      'PRX-8450',
      'Reward points delay',
      'P3',
      { value: 'Pending', tone: 'warning' },
      '2026-09-03T09:10:00+08:00'
    ),
    ticketRow(
      'PRX-8422',
      'API error on customer sync',
      'P2',
      { value: 'Investigating', tone: 'info' },
      '2026-09-02T16:45:00+08:00'
    )
  ],
  actions: [
    {
      id: 'view_all_tickets',
      label: 'View all active support tickets',
      kind: 'link',
      icon: 'arrow'
    }
  ]
};

const TICKET_RECORD: SupportTicketRecordBlock = {
  type: 'support_ticket_record',
  id: 'ticket-PRX-8491',
  intro: 'Here are the details for ticket PRX-8491.',
  intro_highlights: ['PRX-8491'],
  rows: [
    { label: 'Ticket ID', value: 'PRX-8491' },
    { label: 'Title', value: 'Campaign publish action fails from dashboard' },
    { label: 'Severity', value: 'P2', tone: 'warning', variant: 'pill' },
    { label: 'Status', value: 'Open', tone: 'info' },
    { label: 'Created', value: '05 Sep 2026 14:32' },
    { label: 'Last Updated', value: '06 Sep 2026 10:15' },
    {
      label: 'Description',
      value: 'Publish action fails after campaign review. User remains on the same screen.',
      layout: 'stacked'
    }
  ],
  actions: [
    {
      id: 'open_support_portal',
      label: 'Open in Support Portal',
      kind: 'primary',
      icon: 'open',
      payload: { ticket_id: 'PRX-8491' }
    },
    {
      id: 'refresh_status',
      label: 'Refresh status',
      kind: 'secondary',
      payload: { ticket_id: 'PRX-8491' }
    }
  ]
};

// 6265:4611 draws success only; processing and error reuse the ticket flow's copy (6459:16869 / 16542).
const RENEWAL_RESULTS: Record<ResponseFormStatus, ResponseResult | null> = {
  draft: null,
  processing: { title: 'Processing......' },
  success: {
    title: 'Renewal review requested',
    message:
      'Your renewal review request has been submitted successfully.\nThe Perx account team will contact you to confirm the session.'
  },
  error: { title: 'Something went wrong. Try again later!' }
};

const renewal = (status: ResponseFormStatus): RenewalReviewRequestBlock => ({
  type: 'renewal_review_request',
  id: `renewal-review-${status}`,
  intro: 'I can help you schedule a renewal review. Please confirm the details below.',
  title: 'Renewal Review Request',
  icon: 'calendar',
  status,
  fields: [
    { key: 'tenant', label: 'Tenant', kind: 'text', value: 'Acme Bank' },
    {
      key: 'contract_end',
      label: 'Current Contract End',
      kind: 'text',
      value: '31 Dec 2026'
    },
    {
      key: 'account_value',
      label: 'Account Value',
      kind: 'text',
      value: 'Open',
      tone: 'info'
    },
    {
      key: 'review_window',
      label: 'Preferred Review Window',
      kind: 'month',
      value: '2026-10',
      required: true,
      editable: true,
      icon: 'calendar'
    },
    {
      key: 'notes',
      label: 'Additional Notes (Optional)',
      kind: 'textarea',
      value: 'Please include roadmap, renewal timings and volume forecast.',
      editable: true
    }
  ],
  // 6265:4606 / 4608: both buttons stay drawn above the success banner.
  actions: [
    { id: 'cancel', label: 'Cancel', kind: 'secondary' },
    { id: 'schedule_renewal_review', label: 'Submit Request', kind: 'primary' }
  ],
  result: RENEWAL_RESULTS[status]
});

/**
 * The per-type seeds by name. `showcaseSeed.ts` composes the Pinned test chat from them, so an
 * edit here shows there too: keep the names.
 */
export const SUPPORT_SEEDS = {
  ticketQuestion: TICKET_QUESTION,
  clarifyingQuestion: CLARIFYING_QUESTION,
  clarifyingWithQuickReplies: {
    ...CLARIFYING_QUESTION,
    id: 'ticket-environment-quick-replies',
    options: TICKET_FIELDS[0].options?.map(option => ({
      id: `environment_${option.value}`,
      label: option.label,
      kind: 'secondary' as const,
      payload: { environment: option.value }
    }))
  } satisfies ClarifyingQuestionBlock,
  ticketForm,
  ticketTable: TICKET_TABLE,
  ticketRecord: TICKET_RECORD,
  renewal
};

const ticketChat = (id: string, title: string, status: ResponseFormStatus): IOrbitPageDemoChat => ({
  id,
  title,
  chips: TICKET_CHIPS,
  turns: [seedTurn(TICKET_QUESTION, [CLARIFYING_QUESTION]), seedTurn('UAT', [ticketForm(status)])]
});

export const SUPPORT_DEMO_CHATS: IOrbitPageDemoChat[] = [
  ticketChat('ticket-generation', 'Support ticket generation flow', 'draft'),
  ticketChat('ticket-processing', 'Support ticket: processing', 'processing'),
  ticketChat('ticket-created', 'Support ticket: created', 'success'),
  ticketChat('ticket-error', 'Support ticket: error', 'error'),
  {
    id: 'ticket-list',
    title: 'Support ticket list',
    chips: ['Screen: System & Support', 'Period: Current'],
    turns: [
      seedTurn('Show me our active support tickets.', [TICKET_TABLE]),
      seedTurn('Open ticket PRX-8491.', [TICKET_RECORD])
    ]
  },
  {
    id: 'review-renewal',
    title: 'Review Renewal',
    chips: ['Screen: Business Overview', 'Period: Current'],
    turns: [seedTurn('Our renewal is coming up. Schedule a renewal review.', [renewal('success')])]
  }
];
